import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useReduxAuth';
import { useNotification } from './useNotification';
import { hasuraClient } from '../utils/graphqlClient';
import { GET_TRANSFERABLE_REVENUE_ENTRIES_QUERY } from '../graphql/queries/userRevenueEntries';
import { TRANSFER_REVENUE_ENTRY_MUTATION } from '../graphql/mutations/revenueTransferLog';

/**
 * Ciro Aktar hook'u — sayfada GraphQL detayi tutmamak icin tum veri akisi
 * burada toplanir.
 *
 * Dondurur:
 *   entries          aktarilabilir ciro kayitlari (transfer_logs nested)
 *   isLoading        ilk/yenileme fetch durumu
 *   fetchEntries     manuel yenileme
 *   handleTransfer   tek mutation'da entry update + audit log INSERT
 *
 * Yetki:
 *   - TEAM_MANAGER: where clause sayfa tarafindan degil, hook tarafindan
 *     user.team.team_manager_id ile kisitlanir.
 *   - ROOT/DIRECTOR: Hasura select policy company-scoped filtreler.
 *
 * Spec: ekibin tum aktarilabilir cirolarini getirir (onayli + satis disi).
 * Aktarim yapma + gecmis aktarimlari gorme ayni listede. Daha once
 * aktarilmis olanlar tekrar aktarilamaz (sayfada toast ile engellenir).
 */
const useCiroAktar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const buildWhere = useCallback(() => {
    // Spec:
    //   - Mevcut yil: tum aktarilabilir ciroları goster (aktarilmis dahil)
    //   - Gecmis yillar: yalnizca aktarilmis (transfer_logs >= 1) ciroları
    //     goster (audit). Aktarilmamis eski ciroları listeleme.
    // Onayli + satis disi (yazim/onay sonrasi) + ekip scope.
    const currentYear = new Date().getFullYear();
    const base = {
      status: { _eq: 'APPROVED' },
      ciro_kanali: { _in: ['PROJE_YAZIM_CIROSU', 'ONAY_SONRASI_CIROSU'] },
      _or: [
        { yil: { _eq: currentYear } },
        { _and: [{ yil: { _lt: currentYear } }, { transfer_logs: {} }] },
      ],
    };
    if (user?.role === 'TEAM_MANAGER' && user?.id) {
      base.user = {
        team: { team_manager_id: { _eq: parseInt(user.id) } },
      };
    }
    return base;
  }, [user?.role, user?.id]);

  const fetchEntries = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const data = await hasuraClient.query(
        GET_TRANSFERABLE_REVENUE_ENTRIES_QUERY,
        { where: buildWhere() },
      );
      setEntries(Array.isArray(data?.user_revenue_entries) ? data.user_revenue_entries : []);
    } catch (e) {
      showNotification(t('ciroAktar.errors.loadEntries', 'Ciro kayıtları yüklenirken hata oluştu'), 'error');
      setEntries([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, buildWhere, showNotification, t]);

  useEffect(() => {
    if (user?.id) fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /**
   * Tek atomik mutation: entry.ceyrek/yil update + revenue_transfer_log INSERT.
   * transferred_by Hasura permission preset'inden (x-hasura-user-id) atanir —
   * istemciden gondermiyoruz.
   *
   * @param {object} row  - entry satiri (id, yil, ceyrek)
   * @param {number} targetQuarter - hedef ceyrek (1-4)
   * @param {string|null} note - opsiyonel kullanici notu
   */
  const handleTransfer = useCallback(async (row, targetQuarter, note) => {
    if (!row) return false;
    const targetQ = parseInt(targetQuarter);
    if (!Number.isFinite(targetQ) || targetQ < 1 || targetQ > 4) return false;
    if (targetQ === row.ceyrek) {
      showNotification(
        t('ciroAktar.errors.sameQuarter', 'Hedef çeyrek mevcut çeyrek ile aynı'),
        'warning',
      );
      return false;
    }
    try {
      const userNote = (note || '').trim() || null;
      await hasuraClient.mutate(TRANSFER_REVENUE_ENTRY_MUTATION, {
        entryId: row.id,
        newYear: row.yil,
        newQuarter: targetQ,
        logObject: {
          entry_id: row.id,
          from_year: row.yil,
          from_quarter: row.ceyrek,
          to_year: row.yil,
          to_quarter: targetQ,
          note: userNote,
        },
      });
      showNotification(t('ciroAktar.success', 'Ciro başarıyla aktarıldı'), 'success');
      await fetchEntries();
      return true;
    } catch (e) {
      showNotification(t('ciroAktar.error', 'Ciro aktarılırken hata oluştu'), 'error');
      return false;
    }
  }, [showNotification, t, fetchEntries]);

  return {
    entries,
    isLoading,
    fetchEntries,
    handleTransfer,
  };
};

export default useCiroAktar;
export { useCiroAktar };
